import React from 'react'
import DashboardIcon from '@mui/icons-material/Dashboard'
import HomeIcon from '@mui/icons-material/Home'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AssignmentIcon from '@mui/icons-material/Assignment'
import VideocamIcon from '@mui/icons-material/Videocam'
import InfoIcon from '@mui/icons-material/Info'
import FolderIcon from '@mui/icons-material/Folder'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import DownloadIcon from '@mui/icons-material/Download'
import QuizIcon from '@mui/icons-material/Quiz'
import ForumIcon from '@mui/icons-material/Forum'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import PillIcon from 'src/components-new/shell/PillIcon'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import ScienceIcon from '@mui/icons-material/Science'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import BarChartIcon from '@mui/icons-material/BarChart'
import {PATHS} from 'src/constants/constants'
import {sortBySidebarOrder} from 'src/constants/sidebarOrder'
import {NavItemConfig} from 'src/components-new/shell/types'

export const NEW_PATHS = {
  SUMMARY: '/summary',
  PATIENT_HOME: '/summary-patient',
  HOMEPAGE: '/homepage',
  PORTALS: '/portals-new',
} as const

type SidebarItems = Record<string, any>

interface NavItemSpec {
  key: string
  fallbackLabel: string
  path: string
  icon: React.ReactNode
  badge?: number
}

export function buildNavItems(
  sidebarItems: SidebarItems,
  unreadNotificationsCount?: number,
  userGroup?: string
): NavItemConfig[] {
  const isDoctor = Boolean(userGroup?.toUpperCase().includes('DOCTOR'))
  const specs: NavItemSpec[] = [
    {
      key: 'homepage',
      fallbackLabel: 'Homepage',
      path: NEW_PATHS.HOMEPAGE,
      icon: <HomeIcon />
    },
    {
      key: 'summary',
      fallbackLabel: 'Riepilogo',
      path: NEW_PATHS.SUMMARY,
      icon: isDoctor ? <BarChartIcon /> : <DashboardIcon />
    },
    {
      key: 'summaryPatient',
      fallbackLabel: 'Riepilogo',
      path: NEW_PATHS.PATIENT_HOME,
      icon: <DashboardIcon />
    },
    {
      key: 'export',
      fallbackLabel: 'Export',
      path: PATHS.EXPORT,
      icon: <DownloadIcon />
    },
    {
      key: 'agenda',
      fallbackLabel: 'Agenda',
      path: PATHS.AGENDA,
      icon: <CalendarMonthIcon />
    },
    {
      key: 'patients',
      fallbackLabel: 'Pazienti',
      path: PATHS.PATIENTS,
      icon: <PeopleAltIcon />
    },
    {
      key: 'therapyDiary',
      fallbackLabel: 'Diario paziente',
      path: PATHS.DIARY,
      icon: <MenuBookIcon />
    },
    {
      key: 'patientActivities',
      fallbackLabel: 'Attività',
      path: PATHS.PATIENT_ACTIVITIES,
      icon: <AssignmentIcon />
    },
    {
      key: 'enrollmentHub',
      fallbackLabel: 'Enrollment',
      path: PATHS.ENROLLMENT_HUB,
      icon: <PersonAddIcon />
    },
    {
      key: 'virtualMeetings',
      fallbackLabel: 'Videoconsulti',
      path: PATHS.VIRTUAL_MEETINGS,
      icon: <VideocamIcon />
    },
    {
      key: 'echocardiographicRequests',
      fallbackLabel: 'Richieste esami',
      path: PATHS.ECHOCARDIOGRAPHIC_REQUESTS,
      icon: <MonitorHeartIcon />
    },
    {
      key: 'biologicalSamples',
      fallbackLabel: 'Campioni biologici',
      path: PATHS.TEST_DBS,
      icon: <ScienceIcon />
    },
    {
      key: 'survey',
      fallbackLabel: 'Questionari',
      path: PATHS.QUESTIONNAIRE,
      icon: <QuizIcon />
    },
    {
      key: 'contact',
      fallbackLabel: 'Contattami',
      path: '',
      icon: <PhoneInTalkIcon />
    },
    {
      key: 'documents',
      fallbackLabel: 'Documenti',
      path: PATHS.DOCUMENTS,
      icon: <FolderIcon />
    },
    {
      key: 'myDocuments',
      fallbackLabel: 'I miei documenti',
      path: PATHS.MY_DOCUMENTS,
      icon: <FolderIcon />
    },
    {
      key: 'infoMaterials',
      fallbackLabel: 'Materiali informativi',
      path: PATHS.INFO_MATERIALS,
      icon: <InfoIcon />
    },
    {
      key: 'communications',
      fallbackLabel: 'Comunicazioni',
      path: PATHS.COMMUNICATIONS,
      icon: <ForumIcon />,
      badge: unreadNotificationsCount
    },
    {
      key: 'performances',
      fallbackLabel: 'Performances',
      path: PATHS.PERFORMANCES,
      icon: <AssignmentIcon />
    },
    {
      key: 'analytics',
      fallbackLabel: 'Analytics',
      path: PATHS.ANALYTICS,
      icon: <BarChartIcon />
    },
    {
      key: 'therapies',
      fallbackLabel: 'Terapie',
      path: PATHS.THERAPIES,
      icon: <PillIcon />
    },
    {
      key: 'materialRequests',
      fallbackLabel: 'Richieste materiali',
      path: PATHS.MATERIAL_REQUESTS,
      icon: <Inventory2Icon />
    },
    {
      key: 'medicalCenters',
      fallbackLabel: 'Centri medici',
      path: PATHS.MEDICAL_CENTERS,
      icon: <LocalHospitalIcon />
    },
    {
      key: 'customerUtilities',
      fallbackLabel: 'Utenti',
      path: PATHS.CUSTOMER_USERS,
      icon: <ManageAccountsIcon />
    }
    // profile: SKIP — handled separately, not in sidebar
  ]

  const items = sortBySidebarOrder(specs, userGroup)
    .filter(spec => sidebarItems[spec.key]?.show === true)
    .map(spec => {
      const item: NavItemConfig = {
        key: spec.key,
        label: sidebarItems[spec.key]?.label ?? spec.fallbackLabel,
        path: spec.path,
        icon: spec.icon
      }
      if (spec.badge !== undefined) {
        item.badge = spec.badge
      }
      return item
    })

  // Whichever of the three "home" sections a bare visit to "/" actually lands on —
  // same priority as the <Redirect> fallback in MainRoutingPagesNew (homepage >
  // riepilogo paziente > riepilogo) — is pinned as the first sidebar entry, so the
  // PSP's real landing page always sits at the top even when more than one of the
  // three is enabled at once and would otherwise keep this array's fixed order.
  const HOME_PRIORITY = ['homepage', 'summaryPatient', 'summary']
  const homeKey = HOME_PRIORITY.find(key => items.some(item => item.key === key))
  if (homeKey) {
    const homeIndex = items.findIndex(item => item.key === homeKey)
    const [homeItem] = items.splice(homeIndex, 1)
    items.unshift(homeItem)
  }

  return items
}
